import { useEffect } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import type {
  LoginDto,
  RegisterDto,
  AuthResponseDto,
  UserDto,
  ChangePasswordDto,
  ApiResponse,
} from '@monorepo/shared';
import { apiClient } from '../lib/axios';
import { useAuthStore } from '../stores/auth.store';
import { useUserCompaniesStore } from '../stores/userCompanies.store';
import { useSelectedCompanyStore } from '../stores/selectedCompany.store';
import { queryClient } from '../lib/queryClient';

const authApi = {
  login: async (credentials: LoginDto): Promise<AuthResponseDto> => {
    const { data } = await apiClient.post<AuthResponseDto>('/auth/login', credentials);
    return data;
  },

  register: async (userData: RegisterDto): Promise<AuthResponseDto> => {
    const { data } = await apiClient.post<AuthResponseDto>('/auth/register', userData);
    return data;
  },

  logout: async (): Promise<void> => {
    await apiClient.post('/auth/logout');
  },

  getProfile: async (): Promise<UserDto> => {
    const { data } = await apiClient.get<ApiResponse<UserDto>>('/auth/me');
    return data.data!;
  },

  changePassword: async (passwords: ChangePasswordDto): Promise<void> => {
    await apiClient.post('/auth/change-password', passwords);
  },

  deleteAccount: async (): Promise<void> => {
    await apiClient.delete('/auth/account');
  },
};

export const useLogin = () => {
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: authApi.login,
    onSuccess: (data) => {
      setUser(data.user);
    },
  });
};

export const useRegister = () => {
  const setUser = useAuthStore((state) => state.setUser);

  return useMutation({
    mutationFn: authApi.register,
    onSuccess: (data) => {
      setUser(data.user);
    },
  });
};

export const useLogout = () => {
  const logout = useAuthStore((state) => state.logout);
  const clearCompanies = useUserCompaniesStore((state) => state.clearCompanies);
  const clearSelectedCompany = useSelectedCompanyStore((state) => state.clearSelectedCompany);

  return useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      logout();
      clearCompanies();
      clearSelectedCompany();
      queryClient.clear();
    },
  });
};

export const useProfile = (options?: { enabled?: boolean }) => {
  const setUser = useAuthStore((state) => state.setUser);
  const setCompanies = useUserCompaniesStore((state) => state.setCompanies);
  const setSelectedCompany = useSelectedCompanyStore((state) => state.setSelectedCompany);
  const selectedCompanyId = useSelectedCompanyStore((state) => state.selectedCompanyId);

  const query = useQuery({
    queryKey: ['profile'],
    queryFn: authApi.getProfile,
    enabled: options?.enabled !== undefined ? options.enabled : true,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  const { isSuccess, isError, data } = query;

  useEffect(() => {
    if (!isSuccess || !data) {
      return;
    }

    setUser(data);

    const companies = data.companies ?? [];
    setCompanies(companies);

    if (companies.length === 0) {
      setSelectedCompany(null);
      return;
    }

    const isSelectionStillValid = companies.some((company) => company.id === selectedCompanyId);

    if (!isSelectionStillValid) {
      setSelectedCompany(companies[0]!.id);
    }
  }, [isSuccess, data, selectedCompanyId, setUser, setCompanies, setSelectedCompany]);

  useEffect(() => {
    if (!isError) {
      return;
    }

    setUser(null);
    setCompanies([]);
    setSelectedCompany(null);
  }, [isError, setUser, setCompanies, setSelectedCompany]);

  return query;
};

export const useChangePassword = () => {
  return useMutation({
    mutationFn: authApi.changePassword,
  });
};

export const useDeleteAccount = () => {
  const logout = useAuthStore((state) => state.logout);
  const clearCompanies = useUserCompaniesStore((state) => state.clearCompanies);
  const clearSelectedCompany = useSelectedCompanyStore((state) => state.clearSelectedCompany);

  return useMutation({
    mutationFn: authApi.deleteAccount,
    onSuccess: () => {
      logout();
      clearCompanies();
      clearSelectedCompany();
      queryClient.clear();
    },
  });
};
